/// <reference types="vite/client" />
import type { ReactNode } from 'react';
import { useEffect, useState } from 'react';

import { AppShell, Burger, ColorSchemeScript, MantineProvider, NavLink, Text, Title } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { Notifications } from '@mantine/notifications';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { createRootRoute, HeadContent, Link, Outlet, Scripts, useRouterState } from '@tanstack/react-router';
import { PostHogProvider } from 'posthog-js/react';
import { z } from 'zod';

import { KofiButton } from '@/components/KofiButton';
import { ChartProvider } from '@/contexts/ChartContext';
import { initSentry } from '@/lib/sentry';
import { usePageActionsStore } from '@/store/pageActionsStore';
import type { ChartData } from '@/types/chart';

import cssHref from './__root.css?url';

const rootSearchSchema = z.object({
  showcase: z.boolean().optional(),
});

const EXAMPLE_CHARTS = [
  { label: 'Preflop Equity', path: '/preflop-equity', isChart: true },
  { label: 'Sklansky-Chubukov', path: '/sklansky-chubukov', isChart: true },
  { label: '6-Max Opening', path: '/sixmax-opening', isChart: true },
  { label: '8-Max Opening', path: '/eightmax-opening', isChart: true },
  { label: 'Mixed Strategy', path: '/mixed-strategy', isChart: true },
  { label: 'Hand Combos', path: '/combos', isChart: true },
];

const CUSTOM_CHARTS = [{ label: 'Custom Chart', path: '/custom-chart', isChart: true }];

const NAV_ITEMS = [...EXAMPLE_CHARTS, ...CUSTOM_CHARTS];

const queryClient = new QueryClient();

export const Route = createRootRoute({
  validateSearch: rootSearchSchema,
  head: () => ({
    meta: [
      { charSet: 'utf-8' },
      { name: 'viewport', content: 'width=device-width, initial-scale=1.0, viewport-fit=cover' },
      { name: 'color-scheme', content: 'dark' },
      { title: 'Handscape - 3D Poker Hand Charts' },
      {
        name: 'description',
        content:
          'Visualize poker hand ranges as 3D topographic landscapes. Explore preflop equity, opening ranges, and create custom charts with intuitive controls.',
      },
      {
        name: 'keywords',
        content:
          'poker charts, poker hand charts, 3D poker charts, preflop equity chart, poker range chart, texas holdem charts, poker strategy, opening ranges, hand rankings, poker visualization',
      },
      { property: 'og:type', content: 'website' },
      { property: 'og:url', content: 'https://handscape.poker/' },
      { property: 'og:title', content: 'Handscape - 3D Poker Hand Charts' },
      {
        property: 'og:description',
        content:
          'Visualize poker hand ranges as 3D topographic landscapes. Explore preflop equity, opening ranges, and create custom charts.',
      },
      { property: 'og:image', content: 'https://handscape.poker/og-image.png' },
      { property: 'twitter:card', content: 'summary_large_image' },
      { property: 'twitter:url', content: 'https://handscape.poker/' },
      { property: 'twitter:title', content: 'Handscape - 3D Poker Hand Charts' },
      {
        property: 'twitter:description',
        content:
          'Visualize poker hand ranges as 3D topographic landscapes. Explore preflop equity, opening ranges, and create custom charts.',
      },
      { property: 'twitter:image', content: 'https://handscape.poker/og-image.png' },
    ],
    links: [
      { rel: 'stylesheet', href: cssHref },
      { rel: 'icon', type: 'image/svg+xml', href: '/favicon.svg' },
      { rel: 'canonical', href: 'https://handscape.poker/' },
    ],
  }),
  component: RootComponent,
});

function RootComponent() {
  return (
    <RootDocument>
      <RootLayout />
    </RootDocument>
  );
}

function RootDocument({ children }: Readonly<{ children: ReactNode }>) {
  return (
    <html lang="en" suppressHydrationWarning>
      <head>
        <ColorSchemeScript defaultColorScheme="dark" />
        <HeadContent />
        <script
          type="application/ld+json"
          // biome-ignore lint/security/noDangerouslySetInnerHtml: structured data needs this
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              '@context': 'https://schema.org',
              '@type': 'WebApplication',
              name: 'Handscape',
              url: 'https://handscape.poker',
              description:
                'Visualize poker hand ranges as 3D topographic landscapes. Explore preflop equity, opening ranges, and create custom charts.',
              applicationCategory: 'UtilitiesApplication',
              operatingSystem: 'Web',
              offers: {
                '@type': 'Offer',
                price: '0',
                priceCurrency: 'USD',
              },
              featureList: [
                '3D poker hand visualization',
                'Preflop equity charts',
                'Opening range charts',
                'Custom chart creation',
                'Interactive 3D controls',
              ],
              keywords:
                'poker charts, poker hand charts, 3D poker visualization, preflop equity, poker ranges, texas holdem charts, poker strategy tools',
            }),
          }}
        />
      </head>
      <body>
        <div id="root">{children}</div>
        <Scripts />
      </body>
    </html>
  );
}

function PageActions() {
  const primary = usePageActionsStore((state) => state.primary);
  const secondary = usePageActionsStore((state) => state.secondary);

  if (!primary && !secondary) {
    return null;
  }

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
      {secondary?.()}
      {primary?.()}
    </div>
  );
}

function RootLayout() {
  const routerState = useRouterState();
  const currentPath = routerState.location.pathname;
  const { showcase } = Route.useSearch();
  const [mobileNavOpened, { toggle: toggleMobileNav, close: closeMobileNav }] = useDisclosure(false);
  const [chartData, setChartData] = useState<ChartData | null>(null);

  // Initialize Sentry on client side
  useEffect(() => {
    initSentry();
  }, []);

  // Get current page title for navbar
  const currentPage = NAV_ITEMS.find((item) => item.path === currentPath);
  let defaultTitle = 'Handscape';
  if (currentPath === '/') {
    defaultTitle = 'Handscape';
  } else if (currentPath === '/about') {
    defaultTitle = 'About';
  } else {
    defaultTitle = currentPage?.label ?? 'Handscape';
  }
  const pageTitle = chartData?.name ?? defaultTitle;

  // Update document title
  useEffect(() => {
    if (chartData?.name) {
      document.title = `Handscape - ${chartData.name}`;
    } else {
      document.title = 'Handscape';
    }
  }, [chartData]);

  // Reset chart data when navigating away from chart pages
  useEffect(() => {
    const isChartPage = NAV_ITEMS.some((item) => item.path === currentPath && item.isChart);
    if (!isChartPage) {
      setChartData(null);
    }
  }, [currentPath]);

  // Showcase mode: just render the content without any chrome (for demo recordings)
  if (showcase) {
    return (
      <PostHogProvider
        apiKey={import.meta.env.VITE_PUBLIC_POSTHOG_KEY}
        options={{
          api_host: import.meta.env.VITE_PUBLIC_POSTHOG_HOST,
          defaults: '2025-05-24',
          debug: false,
        }}
      >
        <QueryClientProvider client={queryClient}>
          <MantineProvider defaultColorScheme="dark">
            <Notifications position="top-right" />
            <ChartProvider value={{ chartData, setChartData }}>
              <div style={{ backgroundColor: 'var(--mantine-color-dark-9)', height: '100dvh' }}>
                <Outlet />
              </div>
            </ChartProvider>
          </MantineProvider>
        </QueryClientProvider>
      </PostHogProvider>
    );
  }

  return (
    <PostHogProvider
      apiKey={import.meta.env.VITE_PUBLIC_POSTHOG_KEY}
      options={{
        api_host: import.meta.env.VITE_PUBLIC_POSTHOG_HOST,
        defaults: '2025-05-24',
        debug: false,
      }}
    >
      <QueryClientProvider client={queryClient}>
        <MantineProvider defaultColorScheme="dark">
          <Notifications position="top-right" />
          <ChartProvider value={{ chartData, setChartData }}>
            <AppShell
              navbar={{ width: 200, breakpoint: 'sm', collapsed: { mobile: !mobileNavOpened } }}
              header={{ height: 50 }}
              padding={0}
            >
              <AppShell.Header
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 8,
                  paddingLeft: 16,
                  paddingRight: 16,
                  backgroundColor: 'var(--mantine-color-dark-7)',
                  borderBottom: '1px solid var(--mantine-color-dark-5)',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0, flex: 1 }}>
                  <Burger
                    opened={mobileNavOpened}
                    onClick={toggleMobileNav}
                    hiddenFrom="sm"
                    size="sm"
                    color="white"
                    aria-label="Toggle navigation menu"
                  />
                  <img src="/favicon.svg" alt="Handscape Logo" style={{ width: 28, height: 28, flexShrink: 0 }} />
                  <Title
                    order={4}
                    c="white"
                    style={{
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      fontSize: '1rem',
                    }}
                  >
                    {pageTitle}
                  </Title>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                  <PageActions />
                </div>
              </AppShell.Header>

              <AppShell.Navbar
                style={{
                  backgroundColor: 'var(--mantine-color-dark-8)',
                  borderRight: '1px solid var(--mantine-color-dark-5)',
                }}
              >
                <AppShell.Section p="xs">
                  <NavLink
                    component={Link}
                    to="/"
                    label="Home"
                    active={currentPath === '/'}
                    onClick={closeMobileNav}
                    style={{ borderRadius: 4 }}
                  />
                  <NavLink
                    component={Link}
                    to="/about"
                    label="About"
                    active={currentPath === '/about'}
                    onClick={closeMobileNav}
                    style={{ borderRadius: 4, marginBottom: 12 }}
                  />
                  <Text size="xs" c="dimmed" fw={500} mb="xs" tt="uppercase">
                    Examples
                  </Text>
                  {EXAMPLE_CHARTS.map((item) => (
                    <NavLink
                      key={item.path}
                      component={Link}
                      to={item.path}
                      label={item.label}
                      active={currentPath === item.path}
                      onClick={closeMobileNav}
                      style={{ borderRadius: 4 }}
                    />
                  ))}
                  <Text size="xs" c="dimmed" fw={500} mb="xs" mt="md" tt="uppercase">
                    Custom
                  </Text>
                  {CUSTOM_CHARTS.map((item) => (
                    <NavLink
                      key={item.path}
                      component={Link}
                      to={item.path}
                      label={item.label}
                      active={currentPath === item.path}
                      onClick={closeMobileNav}
                      style={{ borderRadius: 4 }}
                    />
                  ))}
                </AppShell.Section>
              </AppShell.Navbar>

              <AppShell.Main
                style={{
                  backgroundColor: 'var(--mantine-color-dark-9)',
                  height: '100dvh',
                }}
              >
                <Outlet />
              </AppShell.Main>
            </AppShell>
            <KofiButton username="tonelabs" />
          </ChartProvider>
        </MantineProvider>
      </QueryClientProvider>
    </PostHogProvider>
  );
}
